/**
 * Ransom-note effect for the hero name: random letters flip between the
 * loaded families/weights (grotesk, serif, italic), then settle back.
 * On fine pointers it runs while hovering a line; on coarse pointers
 * (no hover) it plays short bursts on a randomized timer while the hero
 * is in view. Skipped for prefers-reduced-motion.
 */

const VARIANTS = [
  'lv-serif',        // Cormorant italic
  'lv-serif-up',     // Cormorant upright, semi-bold
  'lv-thin',         // Archivo 300
  'lv-mono-ish',     // Space Grotesk 500
];

export function initHeroLetters(): void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const title = document.querySelector<HTMLElement>('.hero-title');
  if (!title) return;
  title.setAttribute('aria-label', title.textContent?.replace(/\s+/g, ' ').trim() ?? '');

  const coarsePointer = matchMedia('(pointer: coarse)').matches;
  let heroVisible = true;
  if (coarsePointer) {
    new IntersectionObserver(entries => {
      heroVisible = entries.some(en => en.isIntersecting);
    }).observe(title);
  }

  title.querySelectorAll<HTMLElement>('.hero-line').forEach((line, lineIndex) => {
    const text = line.textContent ?? '';
    line.setAttribute('aria-hidden', 'true');
    line.innerHTML = [...text].map(ch =>
      ch.trim() === '' ? ch : `<span class="hero-letter">${ch}</span>`
    ).join('');

    const letters = Array.from(line.querySelectorAll<HTMLElement>('.hero-letter'));
    let timer: ReturnType<typeof setInterval> | null = null;

    // Lock every letter to its natural width so font swaps can't shift layout
    const lockWidths = () => {
      letters.forEach(l => { l.style.width = ''; });
      const widths = letters.map(l => l.getBoundingClientRect().width);
      letters.forEach((l, i) => { l.style.width = `${widths[i]}px`; });
    };
    void document.fonts.ready.then(lockWidths);
    let resizeT: ReturnType<typeof setTimeout> | null = null;
    window.addEventListener('resize', () => {
      if (resizeT) clearTimeout(resizeT);
      resizeT = setTimeout(lockWidths, 150);
    });

    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        letters.forEach(l => {
          // Each tick: ~40% of letters get a random variant, the rest reset
          l.classList.remove(...VARIANTS);
          if (Math.random() < 0.4) {
            l.classList.add(VARIANTS[Math.floor(Math.random() * VARIANTS.length)]!);
          }
        });
      }, 200);
    };

    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
      letters.forEach(l => l.classList.remove(...VARIANTS));
    };

    if (coarsePointer) {
      // No hover on touch: play a ~1.4s burst every 4-8s while the hero
      // is on screen and the tab is visible. Lines are offset so they
      // don't scramble in sync.
      const scheduleBurst = () => {
        const delay = 4000 + Math.random() * 4000 + lineIndex * 1200;
        setTimeout(() => {
          if (heroVisible && document.visibilityState === 'visible') {
            start();
            setTimeout(() => { stop(); scheduleBurst(); }, 1400);
          } else {
            scheduleBurst();
          }
        }, delay);
      };
      scheduleBurst();
    } else {
      line.addEventListener('pointerenter', start);
      line.addEventListener('pointerleave', stop);
    }
  });
}
